import { Box, Button, FlexLayout, Icon, Input, Modal } from '@deity-io/portal-ui';
import { TabItem, Tabs } from 'components';
import EntityMockData from 'components/EntityDetails/EntityMockData';
import { SaveEntityMockDataModal } from 'components/EntityDetails/SaveEntityMockDataModal';
import type { IThemeColor } from 'components/IconButton/IconButton';
import { IconButton } from 'components/IconButton/IconButton';
import { InputEditor } from 'components/InputEditor/InputEditor';
import { CACHE_FIRST } from 'contracts/constants';
import type { ISchema } from 'contracts/interfaces';
import type React from 'react';
import { useEffect, useState } from 'react';
import {
  useDetectSchemaMutation,
  useGetMockDataLazyQuery,
  useParseSchemaMutation,
  useValidateToSchemaMutation
} from 'services/graphql/generated/hooks';
import ValidationReport from '../Endpoints/RequestExplorer/Tabs/ValidationReport';

type StructureTestingProps = {
  structure?: ISchema;
  structureUuid?: string;
  locked?: boolean;
  packageVersionUuid: string;
  onStructureChange?: (structure: ISchema) => void;
};

export const StructureTesting: React.FC<StructureTestingProps> = ({
  structure,
  structureUuid,
  locked = false,
  packageVersionUuid,
  onStructureChange
}) => {
  const [structureState, setStructureState] = useState<{ mockData?: string; name?: string }>({});
  const [mockDataPickerState, setMockDataPickerState] = useState<{ open: boolean; selected?: string }>({ open: false });
  const [saveMockDataState, setSaveMockDataState] = useState<{ open: boolean }>({ open: false });

  const [getMockData, { data: loadedMockData }] = useGetMockDataLazyQuery(CACHE_FIRST);

  const [detectSchemaMutation, { data: detectSchemaResult, loading: detectingSchema }] = useDetectSchemaMutation();
  const [validateToSchemaMutation, { data: schemaValidationResult, loading: validatingSchema }] =
    useValidateToSchemaMutation();
  const [parseDataMutation, { data: parseDataResult, loading: parsingData }] = useParseSchemaMutation();

  const detectSchema = () => {
    if (structureState.mockData) {
      detectSchemaMutation({
        variables: {
          key: 'tst',
          data: structureState.mockData
        }
      })
        .catch(er => {
          // eslint-disable-next-line no-console
          console.log('Mutation Failed', er);
        })
        .then(fetchResult => {
          if (onStructureChange && fetchResult?.data) {
            // eslint-disable-next-line no-console
            console.log('Detected schema', fetchResult.data.detectDataSchema);
            onStructureChange(fetchResult.data.detectDataSchema);
          }

          return fetchResult;
        });
    }
  };

  useEffect(() => {
    setStructureState({
      ...structureState,
      mockData: loadedMockData?.getMockData.data || '',
      name: loadedMockData?.getMockData.name || ''
    });
  }, [loadedMockData?.getMockData]);

  const validateToSchema = () => {
    if (structureState.mockData) {
      validateToSchemaMutation({
        variables: {
          data: structureState.mockData,
          schema: JSON.stringify(structure)
        }
      }).catch(er => {
        // eslint-disable-next-line no-console
        console.log('Mutation Failed', er);
      });
    }
  };

  const parseData = () => {
    if (structureState.mockData) {
      parseDataMutation({
        variables: {
          data: structureState.mockData,
          schema: JSON.stringify(structure)
        }
      }).catch(er => {
        // eslint-disable-next-line no-console
        console.log('Mutation Failed', er);
      });
    }
  };

  let validateColor: IThemeColor = 'disabled';
  if (!locked && structureState.mockData) {
    validateColor = 'primary';
    if (schemaValidationResult) {
      if (schemaValidationResult.validateToSchema.valid) {
        validateColor = 'success';
        if (schemaValidationResult.validateToSchema.warnings?.length) {
          validateColor = 'warning';
        }
      } else {
        validateColor = 'error';
      }
    }
  }

  return (
    <FlexLayout flexDirection="column" sx={{ padding: '10px' }}>
      <FlexLayout flexDirection="row" alignItems="center">
        {/* <H4>{loadedMockData ? `Test data from "${loadedMockData.getMockData.name}"` : 'Unsaved test data'}</H4> */}

        <Box key="toolButtons" sx={{ marginLeft: 'auto', marginRight: '0' }}>
          <IconButton
            sx={{ marginRight: '15px' }}
            key="detect"
            tooltip="Suggest schema from test data"
            icon="addCircleOutline"
            onClick={detectSchema}
            disabled={locked || !structureState.mockData}
          />
          <IconButton
            key="select"
            tooltip="Load stored test data"
            icon="fileOpen"
            onClick={() => setMockDataPickerState({ ...mockDataPickerState, open: true })}
            disabled={locked}
          />
          <IconButton
            key="save"
            tooltip="Save data"
            icon="save"
            onClick={() => {
              setSaveMockDataState({ ...saveMockDataState, open: true });
            }}
            disabled={locked || !structureState.mockData}
          />

          <IconButton
            key="validate"
            tooltip="Validate data"
            icon="checkCircle"
            color={validateColor}
            onClick={validateToSchema}
            disabled={locked || !structureState.mockData}
          />

          <IconButton
            key="parse"
            tooltip="Parse data"
            icon="playArrow"
            onClick={parseData}
            disabled={locked || !structureState.mockData}
          />
        </Box>
      </FlexLayout>
      <Tabs>
        <TabItem label="data" key="DataTab" isActive keepAlive>
          <InputEditor
            value={structureState.mockData}
            disabled={locked}
            allowEmpty={false}
            onChange={value => setStructureState({ ...structureState, mockData: value })}
          />
          {/* <Input
            value={structureState.mockData}
            multiline
            fullWidth
            minRows={10}
            onChange={e => {
              setStructureState({ ...structureState, mockData: e.target.value });
            }}
          /> */}
        </TabItem>
        <TabItem label="validation" key="ValidationTab" keepAlive isActive={false} disabled={!structureState.mockData}>
          {schemaValidationResult ? (
            <ValidationReport report={schemaValidationResult.validateToSchema} refresh={validateToSchema} />
          ) : (
            <FlexLayout
              width="full"
              justifyContent="center"
              alignItems="center"
              sx={{ minHeight: '100px', textAlign: 'center' }}
            >
              <Button endIcon="checkCircle" onClick={validateToSchema}>
                Validate
              </Button>
            </FlexLayout>
          )}
        </TabItem>
        <TabItem label="output" key="OutputTab" keepAlive isActive={false} disabled={!structureState.mockData}>
          {parseDataResult ? (
            <Input value={JSON.stringify(parseDataResult.parseData, null, 4) || ''} multiline fullWidth minRows={10} />
          ) : (
            <FlexLayout
              width="full"
              justifyContent="center"
              alignItems="center"
              sx={{ minHeight: '100px', textAlign: 'center' }}
            >
              <Button endIcon="playArrow" onClick={parseData}>
                Parse data
              </Button>
            </FlexLayout>
          )}
        </TabItem>
      </Tabs>
      {mockDataPickerState && (
        <Modal
          isOpen={mockDataPickerState.open}
          onClose={() => setMockDataPickerState({ ...mockDataPickerState, open: false })}
          title="Select mock data to load."
          closeIcon={<Icon icon="close" />}
          confirmText="Load"
          onConfirm={props => {
            if (mockDataPickerState.selected) {
              getMockData({ variables: { packageVersionUuid, uuid: mockDataPickerState.selected } });
            }
            setMockDataPickerState({ ...mockDataPickerState, open: false, selected: undefined });
          }}
        >
          <EntityMockData
            packageVersionUuid={packageVersionUuid}
            modelUuid={structureUuid}
            onSelect={uuid => {
              setMockDataPickerState({ ...mockDataPickerState, selected: uuid });
            }}
          />
        </Modal>
      )}
      {saveMockDataState.open && structureState.mockData && (
        <SaveEntityMockDataModal
          isOpen={saveMockDataState.open}
          setOpenState={isOpen => setSaveMockDataState({ ...saveMockDataState, open: isOpen })}
          packageVersionUuid={packageVersionUuid}
          modelUuid={structureUuid}
          onMockDataSaved={uuid => {
            getMockData({ variables: { packageVersionUuid, uuid } });
          }}
          editMockData={loadedMockData}
          mockData={structureState.mockData}
        />
      )}
    </FlexLayout>
  );
};
